import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faClose, faEnvelope, faPaperPlane, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  PhoneInput,
  Textarea,
} from '@playcastdotio/Shadcn';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import clsx from 'clsx';
import type { BeaconOutboundManager, OutboundQuotaState } from '../managers/BeaconOutboundManager';

type BeaconInviteModuleProps = {
  title: string;
  buttonText: string;
  buttonIcon: IconProp;
  placeholder: string;
  type: 'email' | 'sms';
  outboundManager: BeaconOutboundManager;
  disabled?: boolean;
};

const isValidEmail = (email: string): boolean => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
const isValidPhoneNumber = (phone: string): boolean => /^\+[\d\s\-()]+$/.test(phone);
const formatPhoneNumber = (phone: string): string => phone.replace(/[\s\-()]/g, '');

export const BeaconInviteModule = ({
  title,
  buttonText,
  buttonIcon,
  placeholder,
  type,
  outboundManager,
  disabled,
}: BeaconInviteModuleProps) => {
  const targetErrorMsg = type === 'email' ? 'Invalid email address' : 'Invalid phone number';
  const messageErrorMsg = 'Message exceeds 200 characters';

  const defaultMessage = "You're invited to join this Playcast session";
  const [target, setTarget] = useState('');
  const [message, setMessage] = useState(defaultMessage);
  const [validMessage, setValidMessage] = useState(true);
  const [validTarget, setValidTarget] = useState(true);
  const [formError, setFormError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quotaState, setQuotaState] = useState<OutboundQuotaState>(outboundManager.getQuotaState());

  const limitExceeded = type === 'email' ? quotaState.reachedEmailLimit : quotaState.reachedSmsLimit;
  const remaining = type === 'email'
    ? quotaState.emailLimit - quotaState.emailCount
    : quotaState.smsLimit - quotaState.smsCount;

  useEffect(() => {
    const listenerId = outboundManager.addListener(setQuotaState);
    return () => outboundManager.removeListener(listenerId);
  }, [outboundManager]);

  const updateFormError = (targetValid: boolean, messageValid: boolean) => {
    if (!targetValid && !messageValid) {
      setFormError(`${targetErrorMsg} and ${messageErrorMsg}`);
    } else if (!targetValid) {
      setFormError(targetErrorMsg);
    } else if (!messageValid) {
      setFormError(messageErrorMsg);
    } else {
      setFormError('');
    }
  };

  const handleTargetChanged = (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = typeof value === 'string' ? value : (value?.target?.value ?? '');
    let valid = false;

    if (type === 'email') {
      valid = isValidEmail(inputValue);
      setTarget(inputValue);
    } else {
      valid = isValidPhoneNumber(inputValue);
      setTarget(formatPhoneNumber(inputValue));
    }

    setValidTarget(valid);
    updateFormError(valid, validMessage);
  };

  const handleMessageChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const valid = e.target.value.length <= 200;
    setValidMessage(valid);
    updateFormError(validTarget, valid);
    if (valid) setMessage(e.target.value);
  };

  const handleSend = () => {
    if (!validMessage || !validTarget) return;
    if (type === 'email') {
      outboundManager.sendEmail(target, message);
    } else {
      outboundManager.sendSms(target, message);
    }
    toast(<div className='text-emerald-400'>{type === 'email' ? 'Email' : 'SMS'} sent successfully</div>);
    setTarget('');
    setMessage('');
    setFormError('');
    setValidMessage(true);
    setValidTarget(true);
    setDialogOpen(false);
  };

  return (
    <Dialog open={dialogOpen}>
      <DialogTrigger asChild>
        <button
          className='group flex items-center'
          onMouseDown={(e) => {
            e.stopPropagation();
            if (!disabled) setDialogOpen(true);
          }}
        >
          <div className='group-hover:border-midnight-bright flex w-full cursor-pointer gap-2 rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'>
            <FontAwesomeIcon icon={buttonIcon} />
            <Label className='cursor-pointer'>{buttonText}</Label>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className='border-none bg-transparent text-center sm:max-w-[425px]' onPointerDownOutside={() => setDialogOpen(false)}>
        <DialogHeader>
          <DialogTitle className="text-primary text-center font-['Syne'] text-2xl">{title}</DialogTitle>
          <DialogDescription className='text-center text-white' />
        </DialogHeader>
        {!limitExceeded && (
          <>
            <DialogDescription className='text-center text-white'>
              {type === 'email' ? `Send an email invite (${remaining} left)` : `Send an SMS invite (${remaining} left)`}
            </DialogDescription>
            <div className='flex flex-col gap-y-4 text-white'>
              <div className='relative items-center'>
                {type === 'email' ? (
                  <Input
                    type='text'
                    placeholder={placeholder}
                    value={target}
                    className={clsx(validTarget ? 'border-emerald-600' : 'border-red-600', 'pr-10')}
                    onChange={handleTargetChanged}
                  />
                ) : (
                  <PhoneInput
                    className={clsx(validTarget ? 'border-emerald-600' : 'border-red-600')}
                    placeholder={placeholder}
                    value={target}
                    onChange={handleTargetChanged}
                    defaultCountry='US'
                  />
                )}
                <FontAwesomeIcon
                  icon={type === 'sms' ? faPhone : faEnvelope}
                  className='absolute right-4 top-5 -translate-y-1/2 transform'
                />
              </div>
              <Textarea
                placeholder='Custom message (0/200)'
                value={message}
                className={validMessage ? 'border-emerald-600' : 'border-red-600'}
                onChange={handleMessageChanged}
              />
            </div>
            {formError && <p className='text-sm text-red-400'>{formError}</p>}
            <DialogFooter>
              <DialogClose asChild className='w-full'>
                <Button
                  type='button'
                  className='m-0 w-full gap-x-2 bg-gray-600 text-white hover:bg-gray-700 focus:bg-gray-500 active:bg-gray-500'
                  onClick={() => {
                    setTarget('');
                    setMessage('');
                    setDialogOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faClose} />
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type='submit'
                className='m-0 w-full gap-x-2 bg-emerald-600 text-white hover:bg-emerald-700 focus:bg-emerald-500 active:bg-emerald-500'
                disabled={!validMessage || !validTarget || limitExceeded}
                onClick={handleSend}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Send
              </Button>
            </DialogFooter>
          </>
        )}
        {limitExceeded && (
          <div className='flex flex-col items-center gap-4'>
            <Label className='text-md text-amber-400'>
              Out of daily {type === 'email' ? 'email' : 'SMS'} messages
            </Label>
            <Label className='text-amber-400'>Please try again later.</Label>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
